// DueTodayNotification.jsx
import React, { useState, useEffect } from "react";
import { X, Clock, AlertCircle, CheckCircle, Circle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";

const DueTodayNotification = ({ task, index, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  useEffect(() => {
    // Stagger entrance animation by index
    const enterTimer = setTimeout(() => setIsVisible(true), index * 120);
    return () => clearTimeout(enterTimer);
  }, [index]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => onDismiss(task.id), 300);
  };

  const handleView = () => {
    navigate(`/tasks/${task.id}`);
    handleDismiss();
  };

  const priorityStyles = {
    low: "border-gray-500 bg-gray-500/10",
    medium: "border-yellow-500 bg-yellow-500/10",
    high: "border-orange-500 bg-orange-500/10",
    urgent: "border-red-500 bg-red-500/10",
  };

  const priorityIconStyles = {
    low: "bg-gray-500/20 text-gray-400",
    medium: "bg-yellow-500/20 text-yellow-400",
    high: "bg-orange-500/20 text-orange-400",
    urgent: "bg-red-500/20 text-red-400",
  };

  const priority = task.priority || "medium";
  const isUrgent = priority === "urgent";

  const StatusIcon =
    task.status === "completed"
      ? CheckCircle
      : task.status === "in_progress"
        ? Clock
        : task.status === "blocked"
          ? AlertCircle
          : Circle;

  return (
    <div
      className={`transition-all duration-300 transform ${
        isVisible
          ? "translate-x-0 opacity-100 scale-100"
          : "translate-x-full opacity-0 scale-95"
      }`}
      style={{ maxWidth: "420px", width: "calc(100vw - 2rem)" }}
    >
      <div
        className={`rounded-xl border-2 shadow-2xl overflow-hidden ${priorityStyles[priority]}`}
        style={{ backgroundColor: isDarkMode ? "#0C0E12" : "#FFFFFF" }}
      >
        {/* Header */}
        <div
          className={`px-5 py-3 border-b flex items-center justify-between ${
            isUrgent
              ? "border-red-500/30 bg-red-500/5 animate-pulse"
              : isDarkMode
                ? "border-gray-700"
                : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${priorityIconStyles[priority]}`}>
              <Clock size={15} />
            </div>
            <span
              className={`text-sm font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Due Today
            </span>
            {isUrgent && (
              <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded uppercase animate-pulse">
                Urgent
              </span>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className={`p-1 transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-3">
          <div className="flex items-start gap-2 mb-2">
            <StatusIcon
              size={16}
              className={`mt-0.5 shrink-0 ${
                task.status === "completed"
                  ? "text-green-400"
                  : task.status === "blocked"
                    ? "text-red-400"
                    : isDarkMode
                      ? "text-gray-400"
                      : "text-gray-500"
              }`}
            />
            <h4
              className={`font-semibold text-sm leading-snug ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {task.title}
            </h4>
          </div>

          {task.description && (
            <p
              className={`text-xs mb-3 line-clamp-2 pl-6 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {task.description}
            </p>
          )}

          <div className="flex gap-2 mt-3">
            <button
              onClick={handleView}
              className="flex-1 px-3 py-2 bg-[#427BCA] hover:bg-[#5A9BE8] text-white text-sm rounded-lg font-medium transition-colors"
            >
              View Task
            </button>
            <button
              onClick={handleDismiss}
              className={`px-3 py-2 text-sm rounded-lg font-medium transition-colors ${
                isDarkMode
                  ? "bg-[#13161B] hover:bg-gray-700 text-gray-300"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-600"
              }`}
            >
              Dismiss
            </button>
          </div>
        </div>

        {/* Priority colour bar at bottom */}
        <div
          className={`h-1 w-full ${
            isUrgent
              ? "bg-red-500"
              : priority === "high"
                ? "bg-orange-500"
                : priority === "medium"
                  ? "bg-yellow-500"
                  : "bg-gray-500"
          }`}
        />
      </div>
    </div>
  );
};

export default DueTodayNotification;
